import { initializeApp } from 'firebase/app'
import { doc, getDoc, getFirestore } from 'firebase/firestore'

import { setConfigs } from './configStore'

const CONFIG_TYPES = ['siteLocation', 'layersConfig'] as const

type ConfigPayload = Record<string, unknown>
type GetToken = (forceRefresh?: boolean) => Promise<string | null>

/**
 * Personal local scratchpad (e.g. `local-emilian-stepien`) — served from the
 * shared Firestore, not the app-config service (AOS-495 hybrid). The fixed
 * `local` / `local-backend` envs are canonical and go through app-config.
 */
function isPersonalLocal(environment: string): boolean {
    return environment.startsWith('local-') && environment !== 'local-backend'
}

async function fetchConfigType(
    baseUrl: string,
    customer: string,
    environment: string,
    configType: (typeof CONFIG_TYPES)[number],
    getToken: GetToken,
): Promise<ConfigPayload> {
    const url = `${baseUrl}/api/app-config/config/${configType}?customer=${customer}&environment=${environment}`

    const fetchOnce = async (forceRefresh?: boolean): Promise<Response> => {
        const token = await getToken(forceRefresh)
        return fetch(url, { headers: { Authorization: `Bearer ${token ?? ''}` } })
    }

    let res = await fetchOnce()
    if (res.status === 401) {
        // The token looked live locally but the server rejected it (expired
        // between acquisition and this request, or revoked server-side) — a
        // plain re-read of the cached token would just resend the same
        // value. Force a real refresh before retrying once, mirroring
        // createAuthorizedFetch's retry-once-on-401 pattern.
        res = await fetchOnce(true)
    }
    if (!res.ok) {
        throw new Error(
            `Config fetch failed: ${url} → ${res.status}. ` +
                'Ensure the config has been published via aos-config-manager.',
        )
    }
    // res.json() throws on a non-JSON body (e.g. an HTML gateway error page
    // that still carried a 2xx) — treat that as an empty payload rather than
    // crashing the whole config load.
    const body = (await res.json().catch(() => ({}))) as { data?: ConfigPayload }
    return body.data ?? {}
}

/** Canonical environments (dev/qa/prod/local) → app-config REST, JWT-authenticated. */
async function loadFromAppConfig(
    customer: string,
    environment: string,
    getToken: GetToken,
): Promise<ConfigPayload[]> {
    // app-config is served same-origin as the frontend (api-gateway routes
    // /api/app-config/** to it), so ORIGIN_URL — already wired per customer ×
    // environment — is the correct base. AOS_APP_CONFIG_URL is an optional
    // override for the rare case app-config moves to a different host.
    const baseUrl = process.env.AOS_APP_CONFIG_URL || process.env.ORIGIN_URL || ''
    if (!baseUrl) {
        throw new Error(
            'Missing app-config base URL: set ORIGIN_URL (or AOS_APP_CONFIG_URL) ' +
                'to the app-config service origin.',
        )
    }
    return Promise.all(
        CONFIG_TYPES.map(configType =>
            fetchConfigType(baseUrl, customer, environment, configType, getToken),
        ),
    )
}

/**
 * Personal local-* envs → shared Firestore (world-readable, no auth). The
 * Firebase app is only initialised on this path; canonical envs never touch it.
 */
async function loadFromFirestore(customer: string, environment: string): Promise<ConfigPayload[]> {
    const apiKey = process.env.SHARED_FIREBASE_API_KEY
    const projectId = process.env.SHARED_FIREBASE_PROJECT_ID
    if (!apiKey || !projectId) {
        throw new Error('Missing SHARED_FIREBASE_* env vars for personal-local config loading.')
    }
    const app = initializeApp(
        { apiKey, authDomain: process.env.SHARED_FIREBASE_AUTH_DOMAIN, projectId },
        'sharedaos-config',
    )
    const db = getFirestore(app)
    return Promise.all(
        CONFIG_TYPES.map(async configType => {
            const docId = `${customer}_${environment}_${configType}`
            const snapshot = await getDoc(doc(db, 'configs', docId))
            if (!snapshot.exists()) {
                throw new Error(
                    `Personal-local config not found: configs/${docId}. ` +
                        'Seed it from config-manager (Local → Seed from <env>).',
                )
            }
            return (snapshot.data().data ?? {}) as ConfigPayload
        }),
    )
}

export async function initConfigs(
    customer: string,
    environment: string,
    getToken: GetToken,
): Promise<void> {
    const results = isPersonalLocal(environment)
        ? await loadFromFirestore(customer, environment)
        : await loadFromAppConfig(customer, environment, getToken)

    setConfigs({
        siteLocation: results[0],
        layersConfig: results[1],
    })
}
